'use client';

/**
 * One slide of {@link ShortsFeed} — a single portrait video plus its
 * bottom-left caption overlay.
 *
 * <h3>Why the player is split out of the feed</h3>
 *
 * The feed owns *which* slide is active; the card owns *how* that slide
 * plays. Keeping them apart means the feed never touches a media
 * element directly — it flips two booleans ({@code active},
 * {@code windowed}) and each card reacts. That's what makes the
 * windowing cheap: a card outside the window renders a poster
 * {@code <div>} and nothing else, so the DOM holds at most
 * {@code windowSize * 2 + 1} media elements no matter how many videos
 * the operator authored.
 *
 * <h3>Two player kinds</h3>
 *
 * <ul>
 *   <li><b>native</b> (.mp4/.webm/…) — a real {@code <video>}. Mounted
 *       for the whole window so neighbours can buffer, but only the
 *       active one is told to play. Autoplay is driven imperatively
 *       (not via the {@code autoPlay} attribute) because we need the
 *       rejected promise to detect a blocked unmuted autoplay.</li>
 *   <li><b>youtube</b> — an iframe. Mounted <em>only</em> while active:
 *       an off-screen YouTube embed costs a full nested document and
 *       buys nothing (there is no cross-origin buffering benefit).
 *       Play/pause/mute afterwards go over the IFrame API postMessage
 *       channel so the iframe never has to reload.</li>
 * </ul>
 *
 * Anything {@link parseVideoSource} can't place (Cinema8 media ids,
 * unknown hosts) falls back to the poster + an "open" link, rather than
 * handing a broken {@code <video src>} to the browser.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Play } from 'lucide-react';
import { tx } from '../_shared/coerce';
import { parseVideoSource } from '../micro/popup/videoSource';

/** Authored copy arrives either as a plain string or as an `{en, tr, …}`
 *  bundle — the admin's translatable-string fields emit the latter. */
type Localized = string | Record<string, string>;

/** One authored video. Only {@code videoUrl} really matters. */
export interface ShortsItem {
  title?: Localized;
  description?: Localized;
  /** Direct file URL (.mp4/.webm/.m4v/.ogv/.mov) or a YouTube URL
   *  (watch / youtu.be / shorts). */
  videoUrl?: string;
  /** Poster frame. Also what renders on the server and for every slide
   *  outside the render window, so it's worth setting. */
  posterUrl?: string;
  /** Optional destination for the caption CTA. */
  href?: string;
  ctaLabel?: Localized;
}

export interface ShortsCardProps {
  item: ShortsItem;
  /** This slide is the one the viewer is looking at. */
  active: boolean;
  /** This slide is inside the render window — mount a real player. */
  windowed: boolean;
  muted: boolean;
  paused: boolean;
  loop: boolean;
  /** `cover` crops to fill the 9:16 stage; `contain` letterboxes. */
  fit: 'cover' | 'contain';
  /** Client-side only. Players stay unmounted during SSR/hydration so
   *  the server HTML (poster) and the first client render agree. */
  mounted: boolean;
  showCaption: boolean;
  /** Unmuted autoplay was rejected — the feed falls back to muted. */
  onAutoplayBlocked: () => void;
  onEnded: () => void;
  /** Tap-anywhere play/pause, wired by the feed. */
  onToggle: () => void;
}

const YT_ORIGIN = 'https://www.youtube-nocookie.com';

/** Pull the video id back out of {@link parseVideoSource}'s embed URL. */
function youtubeId(embedUrl: string): string | undefined {
  return embedUrl.match(/\/embed\/([A-Za-z0-9_-]+)/)?.[1];
}

// ─── YouTube ─────────────────────────────────────────────────────────────────

/**
 * Mounted only while its slide is active. Autoplay params are baked
 * into the initial src; every later state change is a postMessage so
 * the embed never reloads (a reload would restart the clip).
 *
 * Commands sent before the player boots are dropped by YouTube, so we
 * ping `listening` until it answers, then flush.
 */
function YouTubeSlide({
  videoId,
  muted,
  paused,
  loop,
  onEnded,
}: {
  videoId: string;
  muted: boolean;
  paused: boolean;
  loop: boolean;
  onEnded: () => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  // Held in a ref so the handshake effect below keys on `videoId` alone.
  // `onEnded` is rebuilt by the feed whenever the active index moves, and
  // re-running the handshake would restart the ping interval for nothing.
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;

  // Frozen at mount: changing these params would reload the iframe.
  const [src] = useState(() => {
    const p = new URLSearchParams({
      autoplay: '1',
      mute: muted ? '1' : '0',
      controls: '0',
      rel: '0',
      playsinline: '1',
      modestbranding: '1',
      enablejsapi: '1',
      loop: loop ? '1' : '0',
    });
    // YouTube only honours `loop` on a single-video embed when the id is
    // repeated as a one-entry playlist. Undocumented, still true.
    if (loop) p.set('playlist', videoId);
    if (typeof window !== 'undefined') p.set('origin', window.location.origin);
    return `${YT_ORIGIN}/embed/${videoId}?${p.toString()}`;
  });

  const post = (func: string, args: unknown[] = []) => {
    frameRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args }),
      YT_ORIGIN,
    );
  };

  // Handshake: ping until the player answers, and read playback state off
  // the same channel so `onEnded` works without loading the IFrame API JS.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== YT_ORIGIN) return;
      if (e.source !== frameRef.current?.contentWindow) return;
      setReady(true);
      try {
        const payload = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        // 0 = ENDED. Only meaningful when we aren't looping — a looping
        // embed restarts itself and never reports 0.
        if (payload?.info?.playerState === 0) onEndedRef.current();
      } catch {
        /* non-JSON chatter from the embed; ignore */
      }
    };
    window.addEventListener('message', onMessage);

    const ping = () =>
      frameRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: 'listening', id: videoId }),
        YT_ORIGIN,
      );
    ping();
    const timer = window.setInterval(ping, 300);
    // Give up after ~6s. If the player never answers, the embed still
    // plays with its baked-in params — only the toggles go dead.
    const stop = window.setTimeout(() => window.clearInterval(timer), 6000);

    return () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [videoId]);

  useEffect(() => {
    if (!ready) return;
    post(muted ? 'mute' : 'unMute');
  }, [ready, muted]);

  useEffect(() => {
    if (!ready) return;
    post(paused ? 'pauseVideo' : 'playVideo');
  }, [ready, paused]);

  return (
    <iframe
      ref={frameRef}
      src={src}
      title="video"
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        border: 0,
        // The chrome-less embed still swallows clicks; the feed's own
        // tap-to-pause layer sits above it instead.
        pointerEvents: 'none',
      }}
    />
  );
}

// ─── Native <video> ──────────────────────────────────────────────────────────

function NativeSlide({
  url,
  poster,
  active,
  muted,
  paused,
  loop,
  fit,
  onAutoplayBlocked,
  onEnded,
}: {
  url: string;
  poster?: string;
  active: boolean;
  muted: boolean;
  paused: boolean;
  loop: boolean;
  fit: 'cover' | 'contain';
  onAutoplayBlocked: () => void;
  onEnded: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    // Assign rather than rely on the React prop: Safari ignores a
    // `muted` attribute applied after the element already has a source.
    el.muted = muted;

    if (!active || paused) {
      el.pause();
      // Rewind so a slide the viewer comes back to starts from the top,
      // matching what every shorts feed does.
      if (!active) el.currentTime = 0;
      return;
    }

    const attempt = el.play();
    if (attempt) {
      attempt.catch(() => {
        // Chrome/Safari reject unmuted autoplay without a prior user
        // gesture. Tell the feed so it can flip the whole feed to muted
        // and retry — the effect re-runs on the `muted` change.
        if (!el.muted) onAutoplayBlocked();
      });
    }
  }, [active, paused, muted, onAutoplayBlocked]);

  return (
    <video
      ref={videoRef}
      src={url}
      poster={poster}
      loop={loop}
      playsInline
      // Neighbours buffer their headers only; the active slide gets the
      // bandwidth. Without this every windowed slide would race for it.
      preload={active ? 'auto' : 'metadata'}
      onEnded={onEnded}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: fit,
        background: '#000',
      }}
    />
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────

export function ShortsCard({
  item,
  active,
  windowed,
  muted,
  paused,
  loop,
  fit,
  mounted,
  showCaption,
  onAutoplayBlocked,
  onEnded,
  onToggle,
}: ShortsCardProps) {
  const source = parseVideoSource(item.videoUrl);
  const ytId = source.type === 'youtube' ? youtubeId(source.embedUrl) : undefined;

  // Resolve localized bundles once — rendering one raw would print
  // "[object Object]" over the video.
  const itemTitle = tx(item.title);
  const itemDescription = tx(item.description);
  const itemCta = tx(item.ctaLabel);

  const posterStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: item.posterUrl
      ? `center/${fit === 'cover' ? 'cover' : 'contain'} no-repeat url(${item.posterUrl})`
      : 'linear-gradient(160deg, #18181b, #000)',
  };

  // What actually mounts:
  //   native  → whenever this slide is inside the render window
  //   youtube → only while active (see the file header)
  //   neither → poster, always
  const showNative = mounted && windowed && source.type === 'native';
  const showYouTube = mounted && active && !!ytId;

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: '#000' }}>
      {/* Poster stays underneath the player so a decoding video never
          flashes black, and it's the only thing the server renders. */}
      <div aria-hidden style={posterStyle} />

      {showNative && (
        <NativeSlide
          url={source.type === 'native' ? source.url : ''}
          poster={item.posterUrl}
          active={active}
          muted={muted}
          paused={paused}
          loop={loop}
          fit={fit}
          onAutoplayBlocked={onAutoplayBlocked}
          onEnded={onEnded}
        />
      )}

      {showYouTube && (
        <YouTubeSlide videoId={ytId!} muted={muted} paused={paused} loop={loop} onEnded={onEnded} />
      )}

      {/* Tap-anywhere play/pause. Sits above the players (the YouTube
          iframe has pointer-events off for exactly this reason) but below
          the caption's own links. */}
      <button
        type="button"
        onClick={onToggle}
        aria-label={paused ? 'Play' : 'Pause'}
        style={{
          position: 'absolute',
          inset: 0,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          padding: 0,
        }}
      />

      {/* Big play affordance while paused. */}
      {active && paused && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              width: 68,
              height: 68,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(4px)',
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
            }}
          >
            <Play size={30} fill="currentColor" />
          </div>
        </div>
      )}

      {showCaption && (itemTitle || itemDescription) && (
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: '64px 76px 22px 20px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.78), rgba(0,0,0,0) 78%)',
            color: '#fff',
            pointerEvents: 'none',
          }}
        >
          {itemTitle && (
            <div
              style={{
                fontSize: 'var(--font-size-md, 16px)',
                fontWeight: 700,
                lineHeight: 1.3,
                textShadow: '0 1px 3px rgba(0,0,0,0.5)',
              }}
            >
              {itemTitle}
            </div>
          )}
          {itemDescription && (
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                lineHeight: 1.45,
                opacity: 0.88,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {itemDescription}
            </div>
          )}
          {item.href && (
            <a
              href={item.href}
              style={{
                pointerEvents: 'auto',
                display: 'inline-flex',
                marginTop: 12,
                padding: '7px 14px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.16)',
                border: '1px solid rgba(255,255,255,0.28)',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                textDecoration: 'none',
                backdropFilter: 'blur(6px)',
              }}
            >
              {itemCta || 'İzle'}
            </a>
          )}
        </div>
      )}

      {/* Unplayable source — say so instead of shipping a dead <video>. */}
      {mounted && active && source.type !== 'native' && !ytId && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            color: 'rgba(255,255,255,0.65)',
            fontSize: 13,
            padding: 24,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          {item.videoUrl
            ? 'Bu video adresi oynatılamıyor (MP4 veya YouTube bağlantısı kullanın).'
            : 'Video adresi girilmemiş.'}
        </div>
      )}
    </div>
  );
}
